/* eslint-disable no-param-reassign */
import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { defineMessages, useIntl } from 'react-intl';
import audioManager from '/imports/ui/services/audio-manager';
import { useReactiveVar, useMutation } from '@apollo/client';
import useCurrentUser from '/imports/ui/core/hooks/useCurrentUser';
import { ExternalVideoVolumeCommandsEnum } from 'bigbluebutton-html-plugin-sdk/dist/cjs/ui-commands/external-video/volume/enums';
import { SetExternalVideoVolumeCommandArguments } from 'bigbluebutton-html-plugin-sdk/dist/cjs/ui-commands/external-video/volume/types';
import { OnProgressProps } from 'react-player/base';
import * as PluginSdk from 'bigbluebutton-html-plugin-sdk';
import { UI_DATA_LISTENER_SUBSCRIBED } from 'bigbluebutton-html-plugin-sdk/dist/cjs/ui-data-hooks/consts';
import { ExternalVideoVolumeUiDataNames } from 'bigbluebutton-html-plugin-sdk';
import { ExternalVideoVolumeUiDataPayloads } from 'bigbluebutton-html-plugin-sdk/dist/cjs/ui-data-hooks/external-video/volume/types';

import useMeeting from '/imports/ui/core/hooks/useMeeting';
import {
  layoutDispatch,
  layoutSelect,
  layoutSelectInput,
  layoutSelectOutput,
} from '../../layout/context';
import Styled from './styles';
import {
  ExternalVideo,
  Input,
  Layout,
  Output,
} from '../../layout/layoutTypes';
import { uniqueId } from '/imports/utils/string-utils';
import useTimeSync from '/imports/ui/core/local-states/useTimeSync';
import ExternalVideoPlayerToolbar from './toolbar/component';
import deviceInfo from '/imports/utils/deviceInfo';
import { ACTIONS, PRESENTATION_AREA } from '../../layout/enums';
import { EXTERNAL_VIDEO_UPDATE } from '../mutations';
import { calculateCurrentTime } from '/imports/ui/components/external-video-player/service';

import PeerTube from '../custom-players/peertube';
import { ArcPlayer } from '../custom-players/arc-player';
import getStorageSingletonInstance from '/imports/ui/services/storage';
import { smallOnly } from '/imports/ui/stylesheets/styled-components/breakpoints';

const AUTO_PLAY_BLOCK_DETECTION_TIMEOUT_SECONDS = 5;
const UPDATE_INTERVAL_THRESHOLD_MS = 500;

const intlMessages = defineMessages({
  autoPlayWarning: {
    id: 'app.externalVideo.autoPlayWarning',
    description: 'Shown when user needs to interact with player to make it work',
  },
  refreshLabel: {
    id: 'app.externalVideo.refreshLabel',
  },
  fullscreenLabel: {
    id: 'app.externalVideo.fullscreenLabel',
  },
  subtitlesOn: {
    id: 'app.externalVideo.subtitlesOn',
  },
  subtitlesOff: {
    id: 'app.externalVideo.subtitlesOff',
  },
});

interface ExternalVideoPlayerProps {
  currentVolume: React.MutableRefObject<number>;
  isMuted: React.MutableRefObject<boolean>;
  isEchoTest: boolean;
  isGridLayout: boolean;
  isPresenter: boolean;
  videoUrl: string;
  isResizing: boolean;
  fullscreenContext: boolean;
  externalVideo: ExternalVideo;
  playing: boolean;
  playerPlaybackRate: number;
  playerKey: string;
  isSidebarContentOpen: boolean;
  setPlayerKey: (key: string) => void;
  sendMessage: (event: string, data: {
    rate: number | Promise<number>;
    time: number;
    state?: string;
  }) => void;
  getCurrentTime(): number;
}

// @ts-ignore - PeerTubePlayer is not typed
Styled.VideoPlayer.addCustomPlayer(PeerTube);
// @ts-ignore - ArcPlayer is not typed
Styled.VideoPlayer.addCustomPlayer(ArcPlayer);

const truncateTime = (time: number) => (time < 1 ? 0 : time);

const ExternalVideoPlayer: React.FC<ExternalVideoPlayerProps> = ({
  isGridLayout,
  isSidebarContentOpen,
  currentVolume,
  isMuted,
  isResizing,
  externalVideo,
  fullscreenContext,
  videoUrl,
  isPresenter,
  playing,
  playerPlaybackRate,
  isEchoTest,
  playerKey,
  setPlayerKey,
  sendMessage,
  getCurrentTime,
}) => {
  const intl = useIntl();
  const storage = getStorageSingletonInstance();
  const {
    height,
    width,
    top,
    left,
    right,
  } = externalVideo;

  const hideVolume = useMemo(() => ({
    Vimeo: true,
    Facebook: true,
    ArcPlayer: true,
    // YouTube: true,
  }), []);

  const videoPlayConfig = useMemo(() => {
    return {
      // default option for all players, can be overwritten
      playerOptions: {
        autoPlay: true,
        playsInline: true,
        controls: isPresenter,
        muted: true, // Start muted to avoid autoplay blocking
      },
      file: {
        attributes: {
          controls: isPresenter ? 'controls' : '',
          autoPlay: true,
          playsInline: true,
          muted: true, // Start muted to avoid autoplay blocking
        },
      },
      facebook: {
        controls: isPresenter,
        autoplay: true,
      },
      dailymotion: {
        params: {
          controls: isPresenter,
          autoplay: 1,
        },
      },
      youtube: {
        playerVars: {
          autoplay: 1,
          modestbranding: 1,
          autohide: 1,
          rel: 0,
          ecver: 2,
          controls: isPresenter ? 1 : 0,
          cc_lang_pref: document.getElementsByTagName('html')[0].lang.substring(0, 2),
          mute: 1, // Start muted to avoid autoplay blocking
        },
        embedOptions: {
          host: 'https://www.youtube-nocookie.com',
        },
      },
      peertube: {
        isPresenter,
        autoplay: true,
      },
      twitch: {
        options: {
          controls: isPresenter,
          autoplay: true,
        },
        playerId: 'externalVideoPlayerTwitch',
      },
      preload: true,
      showHoverToolBar: false,
    };
  }, [isPresenter]);

  const [showUnsynchedMsg, setShowUnsynchedMsg] = React.useState(false);
  const [showHoverToolBar, setShowHoverToolBar] = React.useState(false);
  const [mute, setMute] = React.useState(true); // Start muted to avoid autoplay blocking
  const [volume, setVolume] = React.useState(1);
  const [subtitlesOn, setSubtitlesOn] = React.useState(false);
  const [played, setPlayed] = React.useState(0);
  const [loaded, setLoaded] = React.useState(0);
  const [autoplayBlocked, setAutoplayBlocked] = React.useState(false);
  const [audioEnabling, setAudioEnabling] = React.useState(false);
  const [audioEnabled, setAudioEnabled] = React.useState(false);
  const playerRef = useRef<ReactPlayer>();
  const playerParentRef = useRef<HTMLDivElement| null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const presenterRef = useRef(isPresenter);
  const [duration, setDuration] = React.useState(0);
  const [reactPlayerPlaying, setReactPlayerPlaying] = React.useState(false);
  const clientReloadedRef = useRef(false);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  let currentTime = getCurrentTime();

  const changeVolume = async (newVolume: number) => {
    setVolume(newVolume);
    storage.setItem('externalVideoVolume', newVolume);
    
    if (newVolume > 0) {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      
      // Try to unmute the internal player
      if (internalPlayer?.unMute && typeof internalPlayer.unMute === 'function') {
        internalPlayer.unMute();
      } else if (internalPlayer?.unmute && typeof internalPlayer.unmute === 'function') {
        internalPlayer.unmute();
      }
      
      // Unmute the player when volume is increased
      if (mute) {
        setMute(false);
      }
      
      // If autoplay was blocked, try to enable audio
      if (autoplayBlocked) {
        console.log('Volume increased, attempting to enable audio...');
        await enableAudioForPlayer();
      }
    } else if (newVolume === 0) {
      // Mute the player when volume is set to 0
      setMute(true);
    }
  };

  const handleDuration = (duration: number) => {
    setDuration(duration);
  };

  // Improved autoplay detection and handling
  const detectAutoplayBlocking = useCallback(async () => {
    try {
      // Try to play a silent video to test autoplay capability
      const testVideo = document.createElement('video');
      testVideo.muted = true;
      testVideo.src = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW9wNDEAAAAIZnJlZQAAAGhtZGF0AAACmwYF//+p3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE0OCByMjYzOSBhOWE1OTBlIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxNSAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnLzEgY29kZWMgLSBjb3B5bGVmdCAyMDAzLTIwMTUgLSBodHRwOi8vd3d3LnZpZGVvbGFuLm9yZy8gLSBpdHR1bmVzOiBTYW5zIFNlcmxpZiBNZWRpdW0gLSBvcmlnaW5hbCBzY3JpcHQgZGF0ZSAyMDAzLTA4LTEgLSBkZWFkIDYgZGF5cyAtIGh0dHA6Ly93d3cudmlkZW9uLm9yZy8=';
      testVideo.load();
      
      const playPromise = testVideo.play();
      if (playPromise !== undefined) {
        await playPromise;
        // Autoplay works, now try to enable audio for the actual player
        await enableAudioForPlayer();
        setAutoplayBlocked(false);
      }
    } catch (error) {
      // Autoplay is blocked
      setAutoplayBlocked(true);
      setMute(true);
      console.warn('Autoplay blocked by browser policy');
    }
  }, []);

  // Function to actually enable audio for the external video player
  const enableAudioForPlayer = useCallback(async () => {
    setAudioEnabling(true);
    
    try {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      if (!internalPlayer) {
        setAudioEnabling(false);
        return false;
      }

      console.log('Attempting to enable audio for external video player...');

      // Try to unmute the internal player
      if (typeof internalPlayer.unMute === 'function') {
        internalPlayer.unMute();
        console.log('Called unMute() on internal player');
      } else if (typeof internalPlayer.unmute === 'function') {
        internalPlayer.unmute();
        console.log('Called unmute() on internal player');
      }

      // Set volume to a reasonable level if it's 0
      if (internalPlayer.volume === 0 || internalPlayer.volume === undefined) {
        if (typeof internalPlayer.setVolume === 'function') {
          internalPlayer.setVolume(50); // Set to 50%
          console.log('Set volume to 50% via setVolume()');
        } else if (internalPlayer.volume !== undefined) {
          internalPlayer.volume = 0.5;
          console.log('Set volume to 0.5 directly');
        }
      }

      // Try to resume audio context if suspended
      if (window.AudioContext || (window as any).webkitAudioContext) {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
          console.log('Resumed suspended audio context');
        }
      }

      // Update local state
      setMute(false);
      setVolume(0.5);
      
      // Force a user interaction to enable autoplay
      if (internalPlayer instanceof HTMLVideoElement) {
        // For HTML5 video elements, try to play with sound
        internalPlayer.muted = false;
        internalPlayer.volume = 0.5;
        
        // Create a user interaction event
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true,
        });
        
        // Dispatch the event on the player
        internalPlayer.dispatchEvent(clickEvent);
        
        // Try to play the video
        try {
          await internalPlayer.play();
          console.log('Audio enabled successfully for external video player');
          setAudioEnabled(true);
          setAutoplayBlocked(false);
        } catch (playError) {
          console.warn('Could not play with audio:', playError);
          // Fallback: keep muted but allow user to manually unmute
          setMute(true);
          setAudioEnabled(false);
        }
      } else {
        // For other player types, assume success if we got this far
        console.log('Audio enabled for non-HTML5 player');
        setAudioEnabled(true);
        setAutoplayBlocked(false);
      }
      
      setAudioEnabling(false);
      return true;
    } catch (error) {
      console.error('Failed to enable audio:', error);
      setAudioEnabling(false);
      setAudioEnabled(false);
      return false;
    }
  }, []);

  // Handle user clicking the "Enable Audio" button
  const handleEnableAudio = useCallback(async () => {
    console.log('User clicked Enable Audio button');
    const success = await enableAudioForPlayer();
    
    if (success) {
      setAutoplayBlocked(false);
      setShowUnsynchedMsg(false);
    } else {
      // If we can't enable audio automatically, show a message to the user
      console.warn('Audio could not be enabled automatically. User may need to interact with the video player directly.');
    }
  }, [enableAudioForPlayer]);

  useEffect(() => {
    const storedVolume = storage.getItem('externalVideoVolume');
    if (storedVolume) {
      setVolume(storedVolume as number);
    }
    
    // Detect autoplay capability on mount
    detectAutoplayBlocking();
  }, [detectAutoplayBlocking]);

  // Improved synchronization for viewers
  useEffect(() => {
    if (!isPresenter && playerRef.current && playing) {
      // For viewers, ensure they stay in sync with the presenter
      const syncInterval = setInterval(() => {
        const expectedTime = getCurrentTime();
        const actualTime = playerRef.current?.getCurrentTime() || 0;
        const timeDiff = Math.abs(expectedTime - actualTime);
        
        // If out of sync by more than 2 seconds, seek to correct position
        if (timeDiff > 2) {
          console.log(`Viewer out of sync by ${timeDiff.toFixed(2)}s, seeking to ${expectedTime.toFixed(2)}s`);
          playerRef.current?.seekTo(expectedTime, 'seconds');
        }
      }, 1000); // Check sync every second
      
      return () => clearInterval(syncInterval);
    }
  }, [isPresenter, playing, getCurrentTime]);

  // Additional sync check when video starts playing
  useEffect(() => {
    if (!isPresenter && playing && playerRef.current) {
      // Initial sync when video starts
      const initialSync = setTimeout(() => {
        const expectedTime = getCurrentTime();
        const actualTime = playerRef.current?.getCurrentTime() || 0;
        const timeDiff = Math.abs(expectedTime - actualTime);
        
        if (timeDiff > 1) {
          console.log(`Initial sync: viewer out of sync by ${timeDiff.toFixed(2)}s, seeking to ${expectedTime.toFixed(2)}s`);
          playerRef.current?.seekTo(expectedTime, 'seconds');
        }
      }, 500); // Wait 500ms for player to stabilize
      
      return () => clearTimeout(initialSync);
    }
  }, [isPresenter, playing, getCurrentTime]);

  // Network latency compensation for better sync
  const [networkLatency, setNetworkLatency] = React.useState(0);
  
  // Measure network latency periodically
  useEffect(() => {
    if (!isPresenter && playing) {
      const latencyInterval = setInterval(() => {
        const startTime = Date.now();
        // This is a simple ping mechanism - in production you might want to use a more sophisticated approach
        const expectedTime = getCurrentTime();
        const actualTime = playerRef.current?.getCurrentTime() || 0;
        const timeDiff = Math.abs(expectedTime - actualTime);
        
        // Estimate network latency based on sync drift
        if (timeDiff > 0.5) {
          setNetworkLatency(Math.min(timeDiff * 0.5, 2000)); // Cap at 2 seconds
        }
      }, 5000); // Check every 5 seconds
      
      return () => clearInterval(latencyInterval);
    }
  }, [isPresenter, playing, getCurrentTime]);

  // Improved synchronization with network latency compensation
  useEffect(() => {
    if (!isPresenter && playerRef.current && playing) {
      const syncInterval = setInterval(() => {
        const expectedTime = getCurrentTime();
        const actualTime = playerRef.current?.getCurrentTime() || 0;
        const timeDiff = Math.abs(expectedTime - actualTime);
        
        // Apply network latency compensation
        const compensatedThreshold = 1.5 + (networkLatency / 1000);
        
        // If out of sync by more than compensated threshold, seek to correct position
        if (timeDiff > compensatedThreshold) {
          console.log(`Viewer out of sync by ${timeDiff.toFixed(2)}s (threshold: ${compensatedThreshold.toFixed(2)}s), seeking to ${expectedTime.toFixed(2)}s`);
          playerRef.current?.seekTo(expectedTime, 'seconds');
        }
      }, 1000); // Check sync every second
      
      return () => clearInterval(syncInterval);
    }
  }, [isPresenter, playing, getCurrentTime, networkLatency]);

  // Monitor playing state changes and sync viewers immediately
  useEffect(() => {
    if (!isPresenter && playerRef.current) {
      const internalPlayer = playerRef.current.getInternalPlayer();
      if (!internalPlayer) return;

      console.log(`Viewer sync: presenter playing state changed to ${playing}`);
      
      if (playing) {
        // Presenter started playing, viewer should play
        if (internalPlayer.playVideo && typeof internalPlayer.playVideo === 'function') {
          internalPlayer.playVideo();
        } else if (internalPlayer instanceof HTMLVideoElement) {
          internalPlayer.play();
        }
      } else {
        // Presenter paused, viewer should pause
        if (internalPlayer.pauseVideo && typeof internalPlayer.pauseVideo === 'function') {
          internalPlayer.pauseVideo();
        } else if (internalPlayer instanceof HTMLVideoElement) {
          internalPlayer.pause();
        }
      }
    }
  }, [playing, isPresenter]);

  // Monitor for unsynchronized player state
  useEffect(() => {
    const unsynchedPlayer = reactPlayerPlaying !== playing;
    if (unsynchedPlayer && !!videoUrl) {
      timeoutRef.current = setTimeout(() => {
        setShowUnsynchedMsg(true);
      }, AUTO_PLAY_BLOCK_DETECTION_TIMEOUT_SECONDS * 1000);
    } else {
      setShowUnsynchedMsg(false);
      clearTimeout(timeoutRef.current);
    }
  }, [reactPlayerPlaying, playing, videoUrl]);

  useEffect(() => {
    const handleExternalVideoVolumeSet = ((
      event: Event,
    ) => {
      const customEvent = event as CustomEvent<SetExternalVideoVolumeCommandArguments>;
      changeVolume(customEvent.detail.volume);
    }) as EventListener;
    window.addEventListener(ExternalVideoVolumeCommandsEnum.SET, handleExternalVideoVolumeSet);
    return () => {
      window.removeEventListener(ExternalVideoVolumeCommandsEnum.SET, handleExternalVideoVolumeSet);
    };
  }, []);

  useEffect(() => {
    if (playerRef.current && !isPresenter) {
      playerRef.current.seekTo(truncateTime(currentTime), 'seconds');
    }
  }, [playerRef.current, playing]);

  // --- Plugin related code ---;
  const internalPlayer = playerRef.current?.getInternalPlayer ? playerRef.current?.getInternalPlayer() : null;
  if (internalPlayer && internalPlayer?.isMuted
    && typeof internalPlayer?.isMuted === 'function'
    && internalPlayer?.isMuted() !== isMuted.current) {
    isMuted.current = internalPlayer?.isMuted();
    window.dispatchEvent(new CustomEvent(ExternalVideoVolumeUiDataNames.IS_VOLUME_MUTED, {
      detail: {
        value: internalPlayer?.isMuted(),
      } as ExternalVideoVolumeUiDataPayloads[ExternalVideoVolumeUiDataNames.IS_VOLUME_MUTED],
    }));
  }
  if (internalPlayer && internalPlayer?.getVolume
    && typeof internalPlayer?.getVolume === 'function'
    && internalPlayer?.getVolume() !== currentVolume.current) {
    currentVolume.current = internalPlayer?.getVolume();
    window.dispatchEvent(new CustomEvent(ExternalVideoVolumeUiDataNames.CURRENT_VOLUME_VALUE, {
      detail: {
        value: internalPlayer?.getVolume() / 100,
      } as ExternalVideoVolumeUiDataPayloads[ExternalVideoVolumeUiDataNames.CURRENT_VOLUME_VALUE],
    }));
  }
  // --- End of plugin related code ---

  useEffect(() => {
    if (isPresenter !== presenterRef.current) {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      const playerVolume = internalPlayer?.getVolume();
      const isMuted = internalPlayer?.isMuted();
      // the scale fiven by the player is 0 to 100, but the accepted scale is 0 to 1
      // So we need to divide by 100
      setVolume(playerVolume / 100);
      setMute(isMuted);
      clientReloadedRef.current = true;
      setPlayerKey(uniqueId('react-player'));
      presenterRef.current = isPresenter;
    }
  }, [isPresenter]);

  const handleOnStart = () => {
    currentTime = getCurrentTime();
    playerRef.current?.seekTo(truncateTime(currentTime), 'seconds');
  };

  const handleOnPlay = async () => {
    setReactPlayerPlaying(true);
    if (isPresenter && !playing && !clientReloadedRef.current) {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      const rate = internalPlayer instanceof HTMLVideoElement
        ? internalPlayer.playbackRate
        : internalPlayer?.getPlaybackRate?.() ?? 1;

      const currentTime = played * duration;
      sendMessage('play', {
        rate,
        time: currentTime,
      });
    }

    // For viewers, sync with presenter's playing state
    if (!isPresenter) {
      if (playing) {
        // Presenter is playing, so viewer should play
        const internalPlayer = playerRef.current?.getInternalPlayer();
        if (internalPlayer?.playVideo && typeof internalPlayer.playVideo === 'function') {
          internalPlayer.playVideo();
        } else if (internalPlayer instanceof HTMLVideoElement) {
          internalPlayer.play();
        }
      } else {
        // Presenter is paused, so viewer should pause
        const internalPlayer = playerRef.current?.getInternalPlayer();
        if (internalPlayer?.pauseVideo && typeof internalPlayer.pauseVideo === 'function') {
          internalPlayer.pauseVideo();
        } else if (internalPlayer instanceof HTMLVideoElement) {
          internalPlayer.pause();
        }
      }
    }
    
    if (clientReloadedRef.current) {
      clientReloadedRef.current = false;
      if (!mute && isPresenter) {
        playerRef.current?.getInternalPlayer().unMute();
      }
    }
  };

  const handleOnStop = async () => {
    setReactPlayerPlaying(false);
    if (isPresenter && playing) {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      let rate = internalPlayer instanceof HTMLVideoElement
        ? internalPlayer.playbackRate
        : internalPlayer?.getPlaybackRate?.() ?? 1;

      if (rate instanceof Promise) {
        rate = await rate;
      }

      const currentTime = playerRef.current?.getCurrentTime() ?? 0;
      sendMessage('stop', {
        rate,
        time: currentTime,
      });
    }

    // For viewers, sync with presenter's playing state
    if (!isPresenter) {
      if (playing) {
        // Presenter is playing, so viewer should play
        const internalPlayer = playerRef.current?.getInternalPlayer();
        if (internalPlayer?.playVideo && typeof internalPlayer.playVideo === 'function') {
          internalPlayer.playVideo();
        } else if (internalPlayer instanceof HTMLVideoElement) {
          internalPlayer.play();
        }
      } else {
        // Presenter is paused, so viewer should pause
        const internalPlayer = playerRef.current?.getInternalPlayer();
        if (internalPlayer?.pauseVideo && typeof internalPlayer.pauseVideo === 'function') {
          internalPlayer.pauseVideo();
        } else if (internalPlayer instanceof HTMLVideoElement) {
          internalPlayer.pause();
        }
      }
    }
  };

  const handleProgress = (state: OnProgressProps) => {
    setPlayed(state.played);
    setLoaded(state.loaded);

    if (playing && isPresenter) {
      currentTime = playerRef.current?.getCurrentTime() || 0;
    }
  };

  const handleOnSeek = async (seconds: number) => {
    if (isPresenter) {
      let rate = playerRef.current?.getInternalPlayer()?.getPlaybackRate() ?? 1;
      if (rate instanceof Promise) {
        rate = await rate;
      }

      sendMessage('seek', {
        rate,
        time: seconds,
      });
      playerRef.current?.seekTo(seconds, 'seconds');
    }
  };

  const isMinimized = width === 0 && height === 0;

  // @ts-ignore accessing lib private property
  const playerName = playerRef.current && playerRef.current.player
    // @ts-ignore accessing lib private property
    && playerRef.current.player.player && playerRef.current.player.player.constructor.name as string;
  let toolbarStyle = 'hoverToolbar';

  if (deviceInfo.isMobile && !showHoverToolBar) {
    toolbarStyle = 'dontShowMobileHoverToolbar';
  }

  if (deviceInfo.isMobile && showHoverToolBar) {
    toolbarStyle = 'showMobileHoverToolbar';
  }

  const shouldShowTools = () => {
    if (isPresenter || (!isPresenter && isGridLayout && !isSidebarContentOpen) || !videoUrl) {
      return false;
    }
    return true;
  };

  return (
    <Styled.Container
      style={{
         height: smallOnly ? height - 50 : height,
        width,
        top,
        left,
        right,
        zIndex: externalVideo.zIndex,
      }}
      isResizing={isResizing}
      isMinimized={isMinimized}
    >
      <Styled.VideoPlayerWrapper
        fullscreen={fullscreenContext}
        ref={playerParentRef}
        data-test="videoPlayer"
        onClick={async () => {
          // If autoplay is blocked, try to enable audio when user clicks on the video
          if (autoplayBlocked && !audioEnabling) {
            console.log('User clicked on video player, attempting to enable audio...');
            await handleEnableAudio();
          }
        }}
        style={{ cursor: autoplayBlocked ? 'pointer' : 'default' }}
      >

        {
          (showUnsynchedMsg || autoplayBlocked) && shouldShowTools()
            ? (
              <Styled.AutoPlayWarning>
                {autoplayBlocked 
                  ? (audioEnabling 
                      ? 'Enabling audio... Please wait'
                      : audioEnabled 
                        ? 'Audio enabled! You can now adjust volume'
                        : intl.formatMessage(intlMessages.autoPlayWarning) + ' - Click to enable audio'
                    )
                  : intl.formatMessage(intlMessages.autoPlayWarning)
                }
                <button 
                  onClick={() => {
                    if (autoplayBlocked && !audioEnabling) {
                      handleEnableAudio();
                    } else if (!autoplayBlocked) {
                      setShowUnsynchedMsg(false);
                    }
                  }}
                  disabled={audioEnabling}
                  style={{
                    background: audioEnabling ? 'rgba(128,128,128,0.5)' : 'rgba(255,255,255,0.2)',
                    border: '1px solid white',
                    color: 'white',
                    padding: '8px 16px',
                    marginLeft: '10px',
                    borderRadius: '4px',
                    cursor: audioEnabling ? 'not-allowed' : 'pointer',
                    opacity: audioEnabling ? 0.7 : 1
                  }}
                >
                  {audioEnabling 
                    ? 'Enabling...' 
                    : autoplayBlocked 
                      ? (audioEnabled ? 'Audio Enabled' : 'Enable Audio')
                      : 'Dismiss'
                  }
                </button>
                {audioEnabling && (
                  <div style={{
                    marginTop: '10px',
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.8)'
                  }}>
                    If audio doesn't work, try clicking directly on the video player
                  </div>
                )}
              </Styled.AutoPlayWarning>
            )
            : ''
        }
        <Styled.VideoPlayer
          config={videoPlayConfig}
          autoPlay
          playsInline
          url={videoUrl}
          playing={playing}
          playbackRate={playerPlaybackRate}
          key={playerKey}
          height="100%"
          width="100%"
          ref={playerRef}
          volume={volume}
          onStart={handleOnStart}
          onPlay={handleOnPlay}
          onSeek={handleOnSeek}
          onDuration={handleDuration}
          onProgress={handleProgress}
          onPause={handleOnStop}
          onEnded={handleOnStop}
          muted={mute || isEchoTest}
          controls={isPresenter}
        />
        {
          shouldShowTools() ? (
            <ExternalVideoPlayerToolbar
              handleOnMuted={(m: boolean) => setMute(m)}
              handleReload={() => setPlayerKey(uniqueId('react-player'))}
              setShowHoverToolBar={setShowHoverToolBar}
              toolbarStyle={toolbarStyle}
              handleVolumeChanged={changeVolume}
              volume={volume}
              muted={mute || isEchoTest}
              mutedByEchoTest={isEchoTest}
              playing={playing}
              playerName={playerName}
              toggleSubtitle={() => setSubtitlesOn(!subtitlesOn)}
              playerParent={playerParentRef.current}
              played={played}
              loaded={loaded}
              subtitlesOn={subtitlesOn}
              hideVolume={hideVolume[playerName as keyof typeof hideVolume]}
            />
          ) : null
        }
      </Styled.VideoPlayerWrapper>
    </Styled.Container>
  );
};

const ExternalVideoPlayerContainer: React.FC = () => {
  /* eslint no-underscore-dangle: "off" */
  // @ts-ignore - temporary while hybrid (meteor+GraphQl)
  const isEchoTest = useReactiveVar(audioManager._isEchoTest.value) as boolean;
  const { data: currentUser } = useCurrentUser((user) => ({
    presenter: user.presenter,
  }));
  const { data: currentMeeting } = useMeeting((m) => ({
    externalVideo: m.externalVideo,
    layout: m.layout,
  }));
  const currentVolume = React.useRef(0);
  const isMuted = React.useRef(false);
  const hasExternalVideo = useRef(false);
  const lastMessageRef = useRef<{
    event: string;
    rate: number;
    time: number;
    state?: string;
  }>({ event: '', rate: 0, time: 0 });

  const [updateExternalVideo] = useMutation(EXTERNAL_VIDEO_UPDATE);

  const sendMessage = async (event: string, data: { rate: number | Promise<number>; time: number; state?: string }) => {
    const resolvedRate = data.rate instanceof Promise ? await data.rate : data.rate;

    // don't re-send repeated update messages
    if (
      lastMessageRef.current.event === event
      && Math.abs(lastMessageRef.current.time - data.time) < UPDATE_INTERVAL_THRESHOLD_MS
    ) {
      return;
    }

    // don't register to redis a viewer joined message
    if (event === 'viewerJoined') {
      return;
    }

    lastMessageRef.current = { ...data, event, rate: resolvedRate };

    // Use an integer for playing state
    // 0: stopped 1: playing
    // We might use more states in the future
    const state = data.state ? 1 : 0;

    updateExternalVideo({
      variables: {
        status: event,
        rate: resolvedRate,
        time: data.time,
        state,
      },
    });
  };

  useEffect(() => {
    if (!currentMeeting?.externalVideo?.externalVideoUrl && hasExternalVideo.current) {
      layoutContextDispatch({
        type: ACTIONS.SET_PILE_CONTENT_FOR_PRESENTATION_AREA,
        value: {
          content: PRESENTATION_AREA.EXTERNAL_VIDEO,
          open: false,
        },
      });
      hasExternalVideo.current = false;
    } else if (currentMeeting?.externalVideo?.externalVideoUrl && !hasExternalVideo.current) {
      layoutContextDispatch({
        type: ACTIONS.SET_PILE_CONTENT_FOR_PRESENTATION_AREA,
        value: {
          content: PRESENTATION_AREA.EXTERNAL_VIDEO,
          open: true,
        },
      });
      hasExternalVideo.current = true;
    }
  }, [currentMeeting?.externalVideo?.externalVideoUrl]);

  // --- Plugin related code ---
  useEffect(() => {
    // Define functions to first inform ui data hooks that subscribe to these events
    const updateUiDataHookCurrentVolumeForPlugin = () => {
      window.dispatchEvent(new CustomEvent(PluginSdk.ExternalVideoVolumeUiDataNames.CURRENT_VOLUME_VALUE, {
        detail: {
          value: currentVolume.current,
        } as ExternalVideoVolumeUiDataPayloads[PluginSdk.ExternalVideoVolumeUiDataNames.CURRENT_VOLUME_VALUE],
      }));
    };
    const updateUiDataHookIsMutedPlugin = () => {
      window.dispatchEvent(new CustomEvent(PluginSdk.ExternalVideoVolumeUiDataNames.IS_VOLUME_MUTED, {
        detail: {
          value: isMuted.current,
        } as ExternalVideoVolumeUiDataPayloads[PluginSdk.ExternalVideoVolumeUiDataNames.IS_VOLUME_MUTED],
      }));
    };

    // When component mount, add event listener to send first information
    // about these ui data hooks to plugin
    window.addEventListener(
      `${UI_DATA_LISTENER_SUBSCRIBED}-${PluginSdk.ExternalVideoVolumeUiDataNames.CURRENT_VOLUME_VALUE}`,
      updateUiDataHookCurrentVolumeForPlugin,
    );
    window.addEventListener(
      `${UI_DATA_LISTENER_SUBSCRIBED}-${PluginSdk.ExternalVideoVolumeUiDataNames.IS_VOLUME_MUTED}`,
      updateUiDataHookIsMutedPlugin,
    );

    // Before component unmount, remove event listeners for plugin ui data hooks
    return () => {
      window.removeEventListener(
        `${UI_DATA_LISTENER_SUBSCRIBED}-${PluginSdk.ExternalVideoVolumeUiDataNames.CURRENT_VOLUME_VALUE}`,
        updateUiDataHookCurrentVolumeForPlugin,
      );
      window.removeEventListener(
        `${UI_DATA_LISTENER_SUBSCRIBED}-${PluginSdk.ExternalVideoVolumeUiDataNames.IS_VOLUME_MUTED}`,
        updateUiDataHookIsMutedPlugin,
      );
    };
  }, []);
  // --- End of plugin related code ---

  const [timeSync] = useTimeSync();

  const fullscreenElementId = 'ExternalVideo';
  const externalVideo: ExternalVideo = layoutSelectOutput((i: Output) => i.externalVideo);
  const hasExternalVideoOnLayout: boolean = layoutSelectInput((i: Input) => i.externalVideo.hasExternalVideo);
  const cameraDock = layoutSelectInput((i: Input) => i.cameraDock);
  const sidebarContent = layoutSelectInput((i: Input) => i.sidebarContent);
  const { isOpen: isSidebarContentOpen } = sidebarContent;
  const { isResizing } = cameraDock;
  const layoutContextDispatch = layoutDispatch();
  const fullscreen = layoutSelect((i: Layout) => i.fullscreen);
  const { element } = fullscreen;
  const fullscreenContext = (element === fullscreenElementId);
  const [key, setKey] = React.useState(uniqueId('react-player'));
  if (!currentUser || !currentMeeting?.externalVideo || !externalVideo?.display) return null;
  if (!hasExternalVideoOnLayout) return null;
  const playerPlaybackRate = currentMeeting.externalVideo?.playerPlaybackRate ?? 1;
  const isPresenter = currentUser.presenter ?? false;
  const isGridLayout = currentMeeting.layout?.currentLayoutType === 'VIDEO_FOCUS';
  const playing = currentMeeting.externalVideo?.playerPlaying ?? false;
  const videoUrl = currentMeeting.externalVideo?.externalVideoUrl ?? '';

  const getCurrentTime = () => calculateCurrentTime(timeSync, currentMeeting.externalVideo);

  return (
    <ExternalVideoPlayer
      isSidebarContentOpen={isSidebarContentOpen}
      isGridLayout={isGridLayout}
      currentVolume={currentVolume}
      isMuted={isMuted}
      isEchoTest={isEchoTest}
      isPresenter={isPresenter ?? false}
      videoUrl={videoUrl}
      playing={playing}
      playerPlaybackRate={playerPlaybackRate}
      isResizing={isResizing}
      fullscreenContext={fullscreenContext}
      externalVideo={externalVideo}
      getCurrentTime={getCurrentTime}
      playerKey={key}
      setPlayerKey={setKey}
      sendMessage={sendMessage}
    />
  );
};

export default ExternalVideoPlayerContainer;
